import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    FaArrowLeft, FaClock, FaLaptop, FaListUl, FaBullseye, FaChalkboardTeacher,
    FaStar, FaMapMarkerAlt, FaFire, FaRegCommentDots,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import apiClient from '../../utils/axiosPetition';
import Cargando from '../../utils/Cargando';
import BotonSubmitEpayco from '../ePayco/BotonSubmitEpayco';
import { Estrellas } from './RedplikaPlace';
import { alertaGeneral } from '../../utils/alertas';
import { useDataUser } from '../../context/dataUserContext';
import { getAndValidateToken } from '../../utils/tokenValidation';

const formatoPrecio = (valor) => `$${Number(valor || 0).toLocaleString('es-CO')} COP`;

const cleanStr = (value) => {
    if (value == null) return '';
    const s = String(value).trim();
    if (!s || s === 'null' || s === 'undefined') return '';
    return s;
};

const getCorreoSesion = () => {
    const tokenData = getAndValidateToken();
    if (tokenData.isValid) {
        const fromToken = cleanStr(tokenData.decoded?.email) || cleanStr(tokenData.decoded?.username);
        if (fromToken.includes('@')) return fromToken;
    }
    const fromStorage = [
        localStorage.getItem('doctorEmail'),
        localStorage.getItem('patientEmail'),
        localStorage.getItem('email'),
        localStorage.getItem('username'),
    ].map(cleanStr).find((v) => v.includes('@'));
    return fromStorage || '';
};

/** Detalle de una publicación de REDPLIKAPLACE + compra vía ePayco (split vendedor/Redplika) */
const RedplikaPlaceDetalle = () => {
    const { listingId } = useParams();
    const { fullName, doctorId, patientId, role } = useDataUser();
    const [producto, setProducto] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');

    const [comprador, setComprador] = useState({
        nombre: cleanStr(fullName),
        correo: getCorreoSesion(),
    });

    // Valoraciones
    const [reviews, setReviews] = useState([]);
    const [permisoValorar, setPermisoValorar] = useState({ puedeValorar: false, reviewExistente: null });
    const [reviewForm, setReviewForm] = useState({ rating: 0, comentario: '' });
    const [enviandoReview, setEnviandoReview] = useState(false);

    // Prefill nombre/correo desde sesión (token + perfil). Evita "null" de localStorage.
    useEffect(() => {
        let cancelado = false;

        const aplicar = (nombre, correo) => {
            if (cancelado) return;
            setComprador((prev) => ({
                nombre: cleanStr(nombre) || prev.nombre,
                correo: cleanStr(correo)?.includes('@') ? cleanStr(correo) : prev.correo,
            }));
        };

        aplicar(fullName, getCorreoSesion());

        const cargarPerfil = async () => {
            try {
                if (role === 'doctor' && doctorId) {
                    const res = await apiClient.get(`/api/doctor/${doctorId}`);
                    if (cancelado) return;
                    aplicar(res.data?.fullName || fullName, res.data?.email || getCorreoSesion());
                    return;
                }
                if (patientId) {
                    const res = await apiClient.get(`/api/patient/${patientId}`);
                    if (cancelado) return;
                    const nombrePaciente = [res.data?.firstName, res.data?.secondName, res.data?.firstLastName, res.data?.secondLastName]
                        .filter(Boolean)
                        .join(' ')
                        .trim();
                    aplicar(nombrePaciente || fullName, res.data?.email || getCorreoSesion());
                }
            } catch {
                // Si falla el perfil, nos quedamos con token/contexto
            }
        };

        if (localStorage.getItem('token')) {
            cargarPerfil();
        }

        return () => { cancelado = true; };
    }, [fullName, doctorId, patientId, role]);

    useEffect(() => {
        setCargando(true);
        apiClient.get(`/api/marketplace/publicaciones/${listingId}`)
            .then(res => setProducto(res.data))
            .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la publicación'))
            .finally(() => setCargando(false));

        apiClient.get(`/api/marketplace/publicaciones/${listingId}/reviews`)
            .then(res => setReviews(Array.isArray(res.data) ? res.data : []))
            .catch(() => setReviews([]));

        if (localStorage.getItem('token')) {
            apiClient.get(`/api/marketplace/publicaciones/${listingId}/puede-valorar`)
                .then(res => {
                    setPermisoValorar(res.data || { puedeValorar: false });
                    if (res.data?.reviewExistente) {
                        setReviewForm({
                            rating: res.data.reviewExistente.rating,
                            comentario: res.data.reviewExistente.comentario || '',
                        });
                    }
                })
                .catch(() => setPermisoValorar({ puedeValorar: false, reviewExistente: null }));
        }
    }, [listingId]);

    const enviarReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.rating) {
            return alertaGeneral({ titulo: 'Error', messageHtml: 'Selecciona una calificación de 1 a 5 estrellas', isError: true });
        }
        setEnviandoReview(true);
        try {
            await apiClient.post(`/api/marketplace/publicaciones/${listingId}/reviews`, reviewForm);
            Swal.fire('¡Gracias!', 'Tu valoración fue registrada.', 'success');
            const [reviewsRes, productoRes] = await Promise.all([
                apiClient.get(`/api/marketplace/publicaciones/${listingId}/reviews`),
                apiClient.get(`/api/marketplace/publicaciones/${listingId}`),
            ]);
            setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
            setProducto(productoRes.data);
            setPermisoValorar(p => ({ ...p, reviewExistente: { ...reviewForm } }));
        } catch (err) {
            alertaGeneral({ titulo: 'Error', messageHtml: err.response?.data?.message || 'No se pudo guardar la valoración', isError: true });
        } finally {
            setEnviandoReview(false);
        }
    };

    if (cargando) return <Cargando message="Cargando publicación..." />;

    if (error || !producto) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                <h2 className="text-xl font-semibold text-slate-700 mb-3">
                    {error || 'Publicación no encontrada'}
                </h2>
                <Link to="../.." relative="path" className="inline-flex items-center gap-2 text-[#205087] font-medium hover:text-[#1f94c2]">
                    <FaArrowLeft /> Volver a REDPLIKAPLACE
                </Link>
            </div>
        );
    }

    const detalles = producto.detalles || {};
    const compradorValido = comprador.correo && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(comprador.correo);

    const bloquesDetalle = [
        { icono: FaClock, titulo: 'Duración', valor: detalles.duracion },
        { icono: FaLaptop, titulo: 'Modalidad', valor: detalles.modalidad },
        { icono: FaChalkboardTeacher, titulo: 'Metodología', valor: detalles.metodologia },
        { icono: FaBullseye, titulo: 'Objetivos', valor: detalles.objetivos },
        { icono: FaListUl, titulo: 'Contenido', valor: detalles.contenido },
    ].filter(b => b.valor);

    return (
        <div className="min-h-[70vh] bg-gradient-to-b from-slate-50 to-white">
            <div className="w-full px-4 sm:px-6 py-6">
                <Link
                    to="../.."
                    relative="path"
                    className="inline-flex items-center gap-2 text-[#205087] hover:text-[#1f94c2] font-medium text-sm mb-6 transition-colors"
                >
                    <FaArrowLeft className="w-4 h-4" /> Volver a REDPLIKAPLACE
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Columna principal */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            {producto.imagenPrincipal && (
                                <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                                    <img
                                        src={producto.imagenPrincipal}
                                        alt={producto.titulo}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <div className="p-6">
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    {producto.categoria && (
                                        <span className="inline-block bg-blue-50 text-[#205087] text-xs font-semibold px-3 py-1 rounded-full">
                                            {producto.categoria.label}
                                        </span>
                                    )}
                                    {producto.destacado && (
                                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">
                                            <FaFire className="w-3 h-3" /> Destacado
                                        </span>
                                    )}
                                    {producto.ciudad && (
                                        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full">
                                            <FaMapMarkerAlt className="w-3 h-3" /> {producto.ciudad}
                                        </span>
                                    )}
                                </div>
                                <h1 className="text-2xl font-bold text-slate-800 mb-2">{producto.titulo}</h1>
                                {producto.ratingCount > 0 && (
                                    <div className="mb-3">
                                        <Estrellas rating={producto.ratingPromedio} count={producto.ratingCount} size="w-4 h-4" />
                                    </div>
                                )}
                                {producto.resumen && (
                                    <p className="text-slate-600 mb-4">{producto.resumen}</p>
                                )}
                                {producto.descripcion && (
                                    <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line border-t border-slate-100 pt-4">
                                        {producto.descripcion}
                                    </div>
                                )}
                            </div>
                        </div>

                        {bloquesDetalle.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                                <h2 className="text-lg font-bold text-slate-800 mb-4">Detalles</h2>
                                <div className="space-y-4">
                                    {bloquesDetalle.map(({ icono: Icono, titulo, valor }) => (
                                        <div key={titulo} className="flex gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#1f94c2] flex items-center justify-center shrink-0">
                                                <Icono className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-700">{titulo}</p>
                                                <p className="text-sm text-slate-600 whitespace-pre-line">{valor}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {producto.galeria?.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {producto.galeria.map((img, i) => (
                                    <img
                                        key={i}
                                        src={img}
                                        alt={`${producto.titulo} ${i + 1}`}
                                        className="rounded-xl border border-slate-200 object-cover aspect-square"
                                    />
                                ))}
                            </div>
                        )}

                        {/* Valoraciones y comentarios */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
                                <FaRegCommentDots className="text-[#1f94c2]" /> Valoraciones de compradores
                            </h2>
                            {producto.ratingCount > 0 ? (
                                <div className="mb-4">
                                    <Estrellas rating={producto.ratingPromedio} count={producto.ratingCount} size="w-4 h-4" />
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 mb-4">Este producto aún no tiene valoraciones.</p>
                            )}

                            {/* Formulario (solo compradores) */}
                            {permisoValorar.puedeValorar && (
                                <form onSubmit={enviarReview} className="bg-slate-50 rounded-xl p-4 mb-5">
                                    <p className="text-sm font-semibold text-slate-700 mb-2">
                                        {permisoValorar.reviewExistente ? 'Edita tu valoración' : '¿Qué te pareció este producto?'}
                                    </p>
                                    <div className="flex items-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                                                aria-label={`${n} estrellas`}
                                            >
                                                <FaStar className={`w-6 h-6 transition-colors ${n <= reviewForm.rating ? 'text-amber-400' : 'text-slate-300 hover:text-amber-200'}`} />
                                            </button>
                                        ))}
                                    </div>
                                    <textarea
                                        rows={3}
                                        maxLength={1000}
                                        value={reviewForm.comentario}
                                        onChange={(e) => setReviewForm(f => ({ ...f, comentario: e.target.value }))}
                                        placeholder="Cuéntanos tu experiencia (opcional)"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2] mb-3"
                                    />
                                    <button
                                        type="submit"
                                        disabled={enviandoReview}
                                        className="px-5 py-2 rounded-lg font-semibold text-white bg-[#205087] hover:bg-[#1f94c2] transition-colors disabled:opacity-60 text-sm"
                                    >
                                        {enviandoReview ? 'Enviando...' : permisoValorar.reviewExistente ? 'Actualizar valoración' : 'Publicar valoración'}
                                    </button>
                                </form>
                            )}

                            {/* Lista de comentarios */}
                            {reviews.length > 0 && (
                                <div className="space-y-4">
                                    {reviews.map((r) => (
                                        <div key={r._id} className="border-t border-slate-100 pt-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">
                                                    {String(r.nombreComprador || '?').charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-700">{r.nombreComprador || 'Comprador'}</p>
                                                    <div className="flex items-center gap-2">
                                                        <Estrellas rating={r.rating} />
                                                        <span className="text-[11px] text-slate-400">
                                                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('es-CO') : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            {r.comentario && (
                                                <p className="text-sm text-slate-600 mt-1 ml-10">{r.comentario}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Columna compra */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6 sticky top-6">
                            {producto.descuentoPorcentaje > 0 && (
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm text-slate-400 line-through">{formatoPrecio(producto.precio)}</span>
                                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                        -{producto.descuentoPorcentaje}%
                                    </span>
                                </div>
                            )}
                            <p className="text-3xl font-bold text-[#205087] mb-1">
                                {formatoPrecio(producto.precioFinal ?? producto.precio)}
                            </p>
                            <p className="text-xs text-slate-500 mb-5">Pago seguro con ePayco</p>

                            {producto.vendedor && (
                                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-100">
                                    {producto.vendedor.foto ? (
                                        <img
                                            src={producto.vendedor.foto}
                                            alt={producto.vendedor.fullName}
                                            className="w-12 h-12 rounded-full object-cover border border-slate-200"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold">
                                            {String(producto.vendedor.fullName || '?').charAt(0)}
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm font-semibold text-slate-800">{producto.vendedor.fullName}</p>
                                        <p className="text-xs text-slate-500">Profesional de la red REDPLIKA</p>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3 mb-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Tu nombre</label>
                                    <input
                                        type="text"
                                        value={comprador.nombre}
                                        onChange={(e) => setComprador(c => ({ ...c, nombre: e.target.value }))}
                                        placeholder="Nombre completo"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Tu correo *</label>
                                    <input
                                        type="email"
                                        value={comprador.correo}
                                        onChange={(e) => setComprador(c => ({ ...c, correo: e.target.value }))}
                                        placeholder="correo@ejemplo.com"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    />
                                    {!compradorValido && comprador.correo && (
                                        <p className="text-xs text-red-500 mt-1">Ingresa un correo válido</p>
                                    )}
                                </div>
                            </div>

                            <BotonSubmitEpayco
                                correo={comprador.correo}
                                amount={producto.precioFinal ?? producto.precio}
                                descripcion={`REDPLIKAPLACE: ${producto.titulo}`}
                                disabled={!compradorValido}
                                prominent
                                extraData={{
                                    tipo: 'marketplace',
                                    listingId: producto._id,
                                    nombreComprador: comprador.nombre,
                                    compradorUserId: (() => {
                                        const t = getAndValidateToken();
                                        if (t.isValid && t.decoded?.id) return String(t.decoded.id);
                                        return localStorage.getItem('userId') || '';
                                    })(),
                                }}
                            />
                            <p className="text-[11px] text-slate-400 mt-3 text-center">
                                Al comprar aceptas los términos y condiciones de REDPLIKA.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RedplikaPlaceDetalle;
